<template>
  <div class="rule-form-view flex flex-col [&_.rule-form-step_.info-text-component]:w-fit [&_.rule-form-step_.info-text-component]:max-w-full">
    <div v-if="isLoading" class="flex justify-center py-20">
      <loader-circle-icon class="size-8 animate-spin text-primary" />
    </div>

    <template v-if="!isLoading">
      <page-header :title="pageHeaderTitle">
        <template #buttons>
          <ui-button variant="outline" as-child>
            <router-link :to="cancelButtonLink">{{ tr('Cancel') }}</router-link>
          </ui-button>
          <ui-button :disabled="isSaving || ($root.isSupportMode && isLastStep)" @click="() => nextButtonAction()">
            <loader-circle-icon v-if="isSaving" class="animate-spin" />
            {{ stepButtonLabel }}
          </ui-button>
        </template>

        <form-step-tab
          v-if="maxStepNumber > 1"
          :model-value="step - 1"
          :items="formStepTabs"
          :allowed-index="allowedStep - 1"
          stretch
          @update:model-value="onStepTabClick"
        />
      </page-header>

      <component :is="renderedView" ref="formStep" />
    </template>
  </div>
</template>

<script>
import { LoaderCircle } from '@lucide/vue';

import RuleService from '@/services/Rule.js';
import FormStepTab from '@/ui/components/FormStepTab.vue';
import PageHeader from '@/ui/components/PageHeader.vue';

export default {
  name: 'RuleFormView',
  components: {
    FormStepTab,
    LoaderCircleIcon: LoaderCircle,
    PageHeader,
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSaving) {
      this.infoToast(this.tr('Please wait until the save is complete'));
      next(false);
      return;
    }

    next();
  },
  data: () => ({
    isSaving: false,
    isLoading: false,
    skipHashChangeWatcher: false,
  }),
  computed: {
    id() {
      return this.$route.params.id ?? null;
    },
    entity() {
      return this.$store.getters['ruleForm/entity'];
    },
    isNew() {
      return this.$route.path.indexOf('/new/') !== -1;
    },
    isOnRuleFormRoute() {
      return !!this.$route.meta.isRuleForm;
    },
    isDuplicate() {
      return !this.isNew && this.$route.path.endsWith('/duplicate');
    },
    isAiMode() {
      return this.$store.getters['ruleForm/isAiMode'];
    },
    readonlyMode() {
      return this.$root.loggedInUser.level === 'restricted_viewer';
    },
    action() {
      if (!this.entity) {
        return null;
      }

      let { action } = this.entity; // .replace('_', '-');
      if (action === 'delete' || action === 'smart_stop') {
        action = 'stop';
      }

      return action;
    },
    formStepTabs() {
      return this.$store.getters['ruleForm/formStepTabs'];
    },
    maxStepNumber() {
      return this.$store.getters['ruleForm/maxStepNumber'];
    },
    step() {
      return this.$store.getters['ruleForm/step'];
    },
    allowedStep() {
      return this.$store.getters['ruleForm/allowedStep'];
    },
    isLastStep() {
      return this.step >= this.maxStepNumber;
    },
    renderedView() {
      return this.$store.getters['ruleForm/renderedView'];
    },
    pageHeaderTitle() {
      if (this.isNew) {
        return this.tr('Create new :action automation', { ':action': this.tr('automation_type_' + this.action) });
      }
      if (this.isDuplicate) {
        return this.tr('Duplicate :action automation', { ':action': this.tr('automation_type_' + this.action) });
      }

      return this.tr('Edit :action automation', { ':action': this.tr('automation_type_' + this.action) });
    },
    cancelButtonLink() {
      if (this.isNew) {
        return '/automation';
      }

      return '/automation/' + this.id;
    },
    stepButtonLabel() {
      if (this.step !== this.maxStepNumber) {
        return this.tr('Next');
      }

      if (this.isNew || this.isDuplicate) {
        return this.tr('Create');
      }

      if (this.readonlyMode) {
        return this.tr('Close');
      }

      return this.tr('Save');
    },
  },
  watch: {
    '$route.path'() {
      if (!this.isOnRuleFormRoute) {
        return;
      }

      this.$store.commit('ruleForm/reset');
      this.init();
    },
    '$route.hash'(val, oldVal) {
      if (!this.isOnRuleFormRoute) {
        return;
      }

      if (this.isLoading) {
        return;
      }

      if (this.skipHashChangeWatcher) {
        this.skipHashChangeWatcher = false;
        return;
      }

      this.updateStepByUrlHash(val, oldVal);
    },
    async step() {
      await this.$nextTick();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    'entity.ad_account.id'(adAccountId) {
      if (this.action !== 'start') {
        return;
      }

      if (!adAccountId) {
        this.$store.commit('ruleForm/setAudiences', []);
        return;
      }

      this.$store.dispatch('ruleForm/fetchAudiences', adAccountId)
        .catch(err => this.error(err));
    },
    'entity.bidAmountIsAvailable'(val) {
      if (!this.entity) {
        return;
      }

      if (!val) {
        this.entity.settings.adset_bid_amount = null;
      }
    },
    'entity.isRoasGoalAvailable'(val) {
      if (!this.entity) {
        return;
      }

      if (!val) {
        this.entity.settings.adset_roas_goal = null;
      }
    },
  },
  created() {
    this.init();
  },
  unmounted() {
    this.$store.commit('ruleForm/reset');
  },
  methods: {
    init() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      const initParams = {};
      if (this.isNew) {
        const actionInUrl = this.$route.params.action;
        const isAiMode = actionInUrl === 'ai-mode';

        const allowedActions = this.$store.getters['ruleForm/allowedActions'];
        if (!allowedActions.includes(actionInUrl) && !isAiMode) {
          this.$router.replace('/automation');
          return;
        }

        initParams.isNew = true;
        initParams.isAiMode = isAiMode;
        initParams.action = isAiMode ? 'start' : actionInUrl;

        if (isAiMode) {
          if (this.$route.query.goal) {
            initParams.goal = this.$route.query.goal;
          }
        }
      }
      else {
        initParams.id = this.id;
        initParams.isDuplicate = this.isDuplicate;
      }

      this.$store.dispatch('ruleForm/init', initParams)
        .then(resp => {
          if (this.isNew) {
            this.navigateToHash('#step-1');
          }
          else {
            this.updateStepByUrlHash();
          }

          return resp;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoading = false);
    },
    navigateToHash(hash, replace = true) {
      const location = { path: this.$route.path, query: this.$route.query, hash };

      return replace ? this.$router.replace(location) : this.$router.push(location);
    },
    updateStepByUrlHash(val, oldVal) {
      const regex = /^#step-\d$/;
      if (!regex.test(this.$route.hash)) {
        this.navigateToHash('#step-' + this.step);
        return;
      }

      if (regex.test(oldVal) && val) {
        if (val > oldVal && !this.$refs.formStep.validate()) {
          this.skipHashChangeWatcher = true;
          this.navigateToHash(oldVal);
          return;
        }
      }

      const stepInHash = this.$route.hash.replace('#step-', '');
      let newStepValue = parseInt(stepInHash);
      if (isNaN(newStepValue)) {
        newStepValue = 1;
      }

      if (this.step === this.allowedStep && this.step + 1 === newStepValue) {
        this.$store.commit('ruleForm/increaseAllowedStep');
      }

      this.$store.dispatch('ruleForm/goToStep', newStepValue)
        .then(success => {
          if (success) {
            return;
          }

          if (regex.test(oldVal)) {
            this.navigateToHash(oldVal);
          }
          else {
            this.navigateToHash('#step-1');
          }

          return;
        })
        .catch(err => this.error(err));
    },
    onStepTabClick(i) {
      this.navigateToHash('#step-' + (i + 1), false);
    },
    nextButtonAction() {
      if (this.isSaving) {
        return;
      }

      if (!this.$refs.formStep.validate()) {
        return;
      }

      if (!this.isLastStep) {
        this.navigateToHash('#step-' + (this.step + 1), false);
        return;
      }

      if (this.readonlyMode) {
        this.$router.push('/automation');
      }
      else {
        this.save();
      }
    },
    async save() {
      if (this.isSaving) {
        return;
      }

      this.isSaving = true;

      let savePromise;

      const isCreate = this.isNew || this.isDuplicate;
      if (typeof this.$refs.formStep.save === 'function') {
        savePromise = this.$refs.formStep.save();
      }
      else if (this.isAiMode) {
        savePromise = RuleService.createAiMode(
          this.entity,
          this.$store.getters['ruleForm/goal'],
          this.$store.getters['ruleForm/scalingBudget'],
          this.$store.getters['ruleForm/aiAssist'],
        );
      }
      else if (isCreate) {
        savePromise = RuleService.create(this.entity);
      }
      else {
        savePromise = RuleService.update(this.entity);
      }

      savePromise
        .then(resp => {
          const keepTermsOnListView = !this.isNew && !this.isDuplicate;
          this.$store.dispatch('rules/resetView', keepTermsOnListView);

          this.$toast.clear();
          this.$toast.success(this.tr('Successful save'));
          if (this.isNew || this.isDuplicate) {
            if (window.fbq) {
              window.fbq('trackCustom', 'PostBoostCreated');
              window.fbq('trackCustom', 'AutomationCreated');
            }
            if (window.ttq) {
              window.ttq.track('PostBoostCreated');
              window.ttq.track('AutomationCreated');
            }
            if (window.rdt) {
              window.rdt('track', 'Custom', { customEventName: 'AutomationCreated' });
            }

            let hasCreatedAutomation = this.$store.getters['rules/hasAnyRules'];
            if (!hasCreatedAutomation) {
              hasCreatedAutomation = window.localStorage.getItem('hasCreatedAutomation');
            }
            if (hasCreatedAutomation) {
              window.localStorage.setItem('hasCreatedAutomation', 1);
            }
            if (!hasCreatedAutomation) {
              if (window.fbq) {
                window.fbq('trackCustom', 'FirstAutomationCreated');
              }
              if (window.ttq) {
                window.ttq.track('FirstAutomationCreated');
              }
              if (window.rdt) {
                window.rdt('track', 'Custom', { customEventName: 'FirstAutomationCreated' });
              }
              if (window.lintrk) {
                // FirstAutomationCreated conversion.
                window.lintrk('track', { conversion_id: 21984641 });
              }
            }
          }

          this.isSaving = false;
          if (this.isAiMode && resp?.rule_id) {
            this.$router.replace('/automation/' + resp.rule_id);
          }
          else if (resp) {
            this.$router.replace('/automation/' + resp.id);
          }
          else {
            this.$router.replace('/automation');
          }

          return resp;
        })
        .catch(err => {
          this.error(this.tr('An error occurred while saving the automation') + ':<br/>' + err.message);
        })
        .finally(() => this.isSaving = false);
    },
  },
};
</script>
